/**
 * Bookmarks Model - Data model for bookmarked questions
 * 
 * MIGRATION STATUS: Uses Pinia (not Vuex)
 * 
 * ARCHITECTURE:
 * - Extends AbstractPiniaModel for Pinia integration
 * - Manages bookmarks list and marked questions count
 * - Static methods interface with the Pinia store for state management
 * 
 * STORAGE:
 * The marked-question count is stored in Pinia localStorage ("${userEmail}@pinia").
 * The bookmark list itself is kept in memory and reloaded from the API.
 */

import AbstractPiniaModel from '../models/AbstractPiniaModel';
import { useBookmarksStore } from '../pinia/stores/bookmarks';


/**
 * Bookmarks Model Class
 * 
 * Represents the bookmarks list with metadata for navigation
 * 
 * STATIC USAGE (accessing state via Pinia):
 * - Bookmarks.getBookmarksList()              → Get all bookmarks metadata
 * - Bookmarks.setBookmarksList(list)          → Store bookmarks list
 * - Bookmarks.getMarkedQuestionsCount()       → Get count of marked questions
 * - Bookmarks.setMarkedQuestionsCountTo(n)    → Set marked questions count
 * - Bookmarks.incMarkedQuestionsCount()       → Increment marked count
 * - Bookmarks.decMarkedQuestionsCount()       → Decrement marked count
 */
export class Bookmarks extends AbstractPiniaModel {
    //#region Static Methods
        /**
         * Get the Pinia store instance for bookmarks
         * @returns {Object} The bookmarks Pinia store
         */
        static piniaStore() {
            return useBookmarksStore()
        }

        /**
         * Get the bookmarks list with metadata
         * Returns array of bookmarks with: position, questionId, mainCategoryName, subCategoryName
         * @returns {Array} Bookmarks list
         */
        static getBookmarksList() { 
            return this.piniaStore().bookmarksList 
        }

        /**
         * Set the bookmarks list
         * Stores the list in memory. The count is what gets persisted.
         * @param {Array} list - Array of bookmark metadata objects
         */
        static setBookmarksList(list) { 
            this.piniaStore().setBookmarksList(list)
        }

        /**
         * Get the count of marked questions
         * @returns {number} Count of marked questions
         */
        static getMarkedQuestionsCount() { 
            return this.piniaStore().markedQuestionsCount 
        }

        /**
         * Set the count of marked questions
         * @param {number} count - New count value
         */
        static setMarkedQuestionsCountTo(count) { 
            this.piniaStore().setMarkedQuestionsCountTo(count)
        }

        /**
         * Increment the marked questions count by 1
         */
        static incMarkedQuestionsCount() { 
            this.piniaStore().incMarkedQuestionsCount()
        }

        /**
         * Decrement the marked questions count by 1
         */
        static decMarkedQuestionsCount() { 
            this.piniaStore().decMarkedQuestionsCount()
        }
    //#endregion Static Methods
}
